/**
 * Sentences and plain-text formats of the sales-experience changes, in one place.
 *
 * Pure functions over the wire values, so every sentence sales read can be tested:
 * the "Back for you" reasons, the open-for badge, the colleague label, the folded
 * replies, the result of a bulk change, and the text that "Copy reply" and "Copy lines"
 * put on the clipboard.
 *
 * Writing rules: plain business language, "sales" for the people who reply, "I2Q" for what
 * I2Q did, "F&O" for the order system, no em dash.
 */

import type {
  AttentionKind,
  BulkItemResult,
  BulkResult,
  CaseBlocker,
  HsnStatus,
  LineMismatch,
  SuggestedCustomer,
} from "@/lib/api";

const nf = new Intl.NumberFormat("en-GB");

// ─── queue rows ─────────────────────────────────────────────────────────────

/** Why a case is back for the caller, as one sentence. */
export function attentionSentence(kind: AttentionKind, customer?: string | null): string {
  switch (kind) {
    case "back_after_customer":
      return customer
        ? `Lines matched for ${customer}`
        : "Lines matched for the customer you confirmed";
    case "back_from_waiting":
      return "No reply from the customer for 5 working days";
    case "over_1_working_day":
      return "Open more than 1 working day";
    case "customer_chasing":
      return "The customer asks for a reply";
  }
}

/**
 * The change the customer asks for on one line of our quotation (sim360 P34), or null:
 * "Remove from the quotation", "Quantity 6, was 4", "Unchanged".
 */
export function quoteDeltaText(line: {
  quoteDelta?: "remove" | "qty" | "keep" | null;
  qty?: number | null;
  qtyBefore?: number | null;
}): string | null {
  switch (line.quoteDelta) {
    case "remove":
      return "Remove from the quotation";
    case "qty":
      return line.qty != null
        ? `Quantity ${nf.format(line.qty)}${line.qtyBefore != null ? `, was ${nf.format(line.qtyBefore)}` : ""}`
        : "New quantity";
    case "keep":
      return "Unchanged";
    default:
      return null;
  }
}

/** The reasons a row is back for the caller ("over 1 working day" has its own badge). */
export function backForYouReasons(
  kinds: readonly AttentionKind[] | null | undefined,
  customer?: string | null,
): string[] {
  return (kinds ?? [])
    .filter((kind) => kind !== "over_1_working_day")
    .map((kind) => attentionSentence(kind, customer));
}

/** "Open 3 working days"; null for a case open less than one working day or not open. */
export function workingDaysOpenText(days: number | null | undefined): string | null {
  if (typeof days !== "number" || !Number.isFinite(days) || days < 1) {
    return null;
  }
  const whole = Math.floor(days);
  return `Open ${nf.format(whole)} working ${whole === 1 ? "day" : "days"}`;
}

/** "Usually handled by Alex Baker"; null without an owner. A suggestion, never an assignment. */
/**
 * "To Ann Example" for a queue row: whom the customer wrote to (sim360 P22). The first
 * personal address of the To/Cc line, "+n" for more. Null when the mail went only to a
 * shared mailbox. `name` turns an address into the person's name.
 */
export function addressedToText(
  addresses: readonly string[] | null | undefined,
  name: (address: string) => string,
): string | null {
  const list = (addresses ?? []).filter((address) => address.includes("@"));
  const first = list[0];
  if (first === undefined) {
    return null;
  }
  const more = list.length > 1 ? ` +${list.length - 1}` : "";
  return `To ${name(first)}${more}`;
}

export function usualOwnerText(
  owner: { email: string; name: string | null } | null | undefined,
  nameOf: (email: string) => string = (email) => email,
): string | null {
  if (!owner?.email) {
    return null;
  }
  const who = (owner.name ?? "").trim() || nameOf(owner.email) || owner.email;
  return `Usually handled by ${who}`;
}

/**
 * "Sales responsible on the last quotation: <name> (F&O)" when that person is not the
 * usual owner, else null. Both come from F&O but answer different questions (who made
 * most quotations vs who is set on the latest one), so both are shown with their
 * source (360 simulation P22).
 */
export function salesResponsibleNote(
  owner: { email: string; name: string | null } | null | undefined,
  header: readonly { field: string; value: string; name?: string | null }[] | null | undefined,
  nameOf: (email: string) => string = (email) => email,
): string | null {
  const responsible = (header ?? []).find((entry) => entry.field === "sales_responsible");
  if (!owner?.email || !responsible) {
    return null;
  }
  const shown = (responsible.name ?? "").trim() || responsible.value.trim();
  if (!shown) {
    return null;
  }
  const ownerName = ((owner.name ?? "").trim() || nameOf(owner.email)).toLowerCase();
  const same = [shown.toLowerCase(), responsible.value.trim().toLowerCase()].some(
    (candidate) =>
      candidate === ownerName ||
      candidate === owner.email.toLowerCase() ||
      candidate === owner.email.split("@")[0]?.toLowerCase(),
  );
  return same ? null : `Sales responsible on the last quotation: ${shown} (F&O)`;
}

/** The tip of the usual owner: what the suggestion rests on. */
export function usualOwnerTip(
  owner: { quotations: number; total: number } | null | undefined,
): string | null {
  if (!owner) {
    return null;
  }
  return `Made ${nf.format(owner.quotations)} of this customer's ${nf.format(owner.total)} quotations in F&O in the year before this mail. A suggestion only. You decide who takes the case.`;
}

/** "From a colleague: Anna Jensen"; "From a colleague" without a name. */
export function forwardedByText(name: string | null | undefined): string {
  const trimmed = (name ?? "").trim();
  return trimmed ? `From a colleague: ${trimmed}` : "From a colleague";
}

/** The customer column of a colleague's mail without a customer. */
export const CUSTOMER_NOT_IDENTIFIED = "Customer not identified";

/** "+2 replies in this thread"; null without folded replies. */
export function foldedRepliesText(count: number | null | undefined): string | null {
  if (typeof count !== "number" || count < 1) {
    return null;
  }
  const whole = Math.floor(count);
  return `+${nf.format(whole)} ${whole === 1 ? "reply" : "replies"} in this thread`;
}

/**
 * "Acme Foundry, Aarhus"; the account number when the name is missing. `others` are the
 * other accounts offered beside it: when one of them has the same name, the account
 * number follows ("Acme Foundry, Aarhus, account 201234"), so the confirm button names
 * the account it confirms (hunt round 2, Q-27).
 */
export function suggestedCustomerText(
  customer: SuggestedCustomer,
  others: readonly SuggestedCustomer[] = [],
): string {
  const name = (customer.name ?? "").trim() || `Account ${customer.account}`;
  // City and postcode tell two accounts of the same name apart ("Basildon SS16").
  const place = [customer.city, customer.postcode]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(" ");
  const shown = place ? `${name}, ${place}` : name;
  const key = (customer.name ?? "").trim().toLowerCase();
  const twin =
    key !== "" &&
    others.some(
      (other) =>
        other.account !== customer.account && (other.name ?? "").trim().toLowerCase() === key,
    );
  return twin ? `${shown}, account ${customer.account}` : shown;
}

/**
 * True when a suggestion may be confirmed with one click (queue row, the `c` key): it
 * rests on evidence or is certain. A guess (a ranked or weak name match, or an older
 * answer without a strength) is confirmed from the case page with the mail in view
 * (360 simulation P13).
 */
export function confirmsInOneClick(customer: Pick<SuggestedCustomer, "strength">): boolean {
  return customer.strength === "evidence" || customer.strength === "certain";
}

/** "Suggested (guess): <name>, <city> <postcode>" for a suggestion without evidence. */
export function suggestedGuessText(customer: SuggestedCustomer): string {
  return `Suggested (guess): ${suggestedCustomerText(customer)}`;
}

// ─── bulk changes ───────────────────────────────────────────────────────────

/** What a bulk change does, as the queue's action bar names it. */
export type BulkAction = "done" | "not_for_us" | "waiting_customer" | "open" | "take" | "release";

const BULK_DONE: Record<BulkAction, string> = {
  done: "done",
  not_for_us: "set to not for us",
  waiting_customer: "set to waiting for customer",
  open: "reopened",
  take: "taken",
  release: "released",
};

/** Codes that mean "somebody changed the case after you saw it". */
export const CHANGED_CODES: readonly BulkItemResult["code"][] = [
  "version_conflict",
  "assigned_to_other",
];

function failureGroup(code: BulkItemResult["code"]): string {
  if (CHANGED_CODES.includes(code)) {
    return "changed by a colleague in the meantime (not changed)";
  }
  if (code === "not_found") {
    return "missing (not changed)";
  }
  if (code === "reopen_needs_manager") {
    return "closed by a colleague, only a manager can reopen them";
  }
  return "not changed";
}

/** "35 done, 2 changed by a colleague in the meantime (not changed)". */
export function bulkResultText(result: BulkResult, action: BulkAction): string {
  const results = result.results ?? [];
  const ok = results.filter((entry) => entry.code === "ok").length;
  const parts = [`${nf.format(ok)} ${BULK_DONE[action]}`];
  const groups = new Map<string, number>();
  for (const entry of results) {
    if (entry.code !== "ok") {
      const label = failureGroup(entry.code);
      groups.set(label, (groups.get(label) ?? 0) + 1);
    }
  }
  for (const [label, count] of groups) {
    parts.push(`${nf.format(count)} ${label}`);
  }
  return parts.join(", ");
}

/** The ids a colleague changed in the meantime, to select them again. */
export function changedIds(result: BulkResult | null | undefined): string[] {
  return (result?.results ?? [])
    .filter((entry) => CHANGED_CODES.includes(entry.code))
    .map((entry) => entry.case_id);
}

/** The confirm sentence before a bulk close: "Mark 37 cases as done? You can reopen each one." */
export function bulkConfirmText(count: number, action: BulkAction): string {
  const cases = `${nf.format(count)} ${count === 1 ? "case" : "cases"}`;
  if (action === "not_for_us") {
    return `Set ${cases} to not for us? You can reopen each one.`;
  }
  return `Mark ${cases} as done? You can reopen each one.`;
}

// ─── copy-ready text ────────────────────────────────────────────────────────

/** The long dashes, written as code points so this file carries none itself. */
const EM_DASH = String.fromCharCode(0x2014);
const EN_DASH = String.fromCharCode(0x2013);
const SPACED_EM_DASH = new RegExp(`\\s*${EM_DASH}\\s*`, "g");

/** An em dash or en dash as a plain hyphen: the brand guide uses no em dash. */
function plainDashes(text: string): string {
  return text.replace(SPACED_EM_DASH, " - ").split(EN_DASH).join("-");
}

/**
 * What "Copy reply" puts on the clipboard: the subject line, a blank line and the body,
 * as plain text. Placeholders such as `[delivery date]` stay, so sales see what to fill.
 */
export function replyClipboardText(subject: string | null | undefined, body: string): string {
  const cleanSubject = plainDashes((subject ?? "").trim());
  const cleanBody = plainDashes(body.replace(/\r\n/g, "\n")).trim();
  return cleanSubject ? `Subject: ${cleanSubject}\n\n${cleanBody}` : cleanBody;
}

/** One line of a prepared quotation as "Copy lines" needs it. */
export interface CopyLine {
  item: string;
  qty?: number | null;
  unit?: string | null;
  unit_price?: number | string | null;
  currency?: string | null;
  /** Set when the price is per another unit than the quantity, with no verified conversion. */
  unit_issue?: string | null;
}

function cell(value: unknown): string {
  if (value == null) {
    return "";
  }
  return String(value)
    .replace(/[\t\r\n]+/g, " ")
    .trim();
}

/**
 * What "Copy lines" puts on the clipboard: one row per line, tab separated (item,
 * quantity, unit, sales price, currency) under a header row, ready to paste into a
 * grid such as Excel. Only sales prices: never a cost or a margin.
 */
export function linesClipboardText(lines: readonly CopyLine[]): string {
  const rows = [["Item", "Quantity", "Unit", "Sales price", "Currency"].join("\t")];
  for (const line of lines) {
    if (!line.item) {
      continue;
    }
    // A price per another unit than the quantity is never pasted next to it (F03).
    const priced = !line.unit_issue;
    rows.push(
      [
        cell(line.item),
        cell(line.qty),
        cell(line.unit),
        cell(priced ? line.unit_price : null),
        cell(priced ? line.currency : null),
      ].join("\t"),
    );
  }
  return rows.join("\n");
}

// ─── case page ──────────────────────────────────────────────────────────────

/** "Lead time not known for 2 lines"; null when every line has one. */
export function leadTimeUnknownText(count: number): string | null {
  if (count < 1) {
    return null;
  }
  return `Lead time not known for ${nf.format(count)} ${count === 1 ? "line" : "lines"}`;
}

/** "Accept 3 matched items". */
export function acceptMatchedLabel(count: number): string {
  return `Accept ${nf.format(count)} matched ${count === 1 ? "item" : "items"}`;
}

/** The toast after "Accept matched items": "3 lines accepted. Undo per line." */
export function acceptedText(count: number): string {
  if (count < 1) {
    return "No line left to accept.";
  }
  return `${nf.format(count)} ${count === 1 ? "line" : "lines"} accepted. Undo per line.`;
}

/**
 * The sentence after a customer confirmation, under the customer line and as the
 * caption of the one action while I2Q reads the case again (tester round 5, 12 Oct
 * 2026: "Reading the mail" for minutes read as stuck). It says what happens and how long.
 */
export function customerConfirmedText(name: string | null | undefined): string {
  const who = (name ?? "").trim();
  return `I2Q reads the prices for ${who || "this customer"} again. This can take up to 5 minutes. The page shows the result by itself.`;
}

/** The caption while I2Q reads a case again that it had read before (no customer named). */
export const READING_AGAIN_TEXT =
  "I2Q reads this mail again. This can take up to 5 minutes. The rows keep their buttons.";

/** Who prepared a reply without a person asking: the ingest stage. */
export const PREPARED_BY = "i2q-prepared";

/** The label of a reply I2Q prepared when the mail arrived. */
export const PREPARED_ON_ARRIVAL = "Prepared by I2Q on arrival";

/** Shown on a draft made before the last change to the case. */
export const MADE_BEFORE_CHANGES = "Made before your changes";

/**
 * Why there is no draft yet on a case nothing else blocks (DK manager review MD-05 and
 * SA-07, 12 Oct 2026). The reply panel and the decision strip print the same sentence.
 */
export const DRAFT_ON_REQUEST_TEXT = "I2Q prepares the reply when you ask for it.";

/** The button of the reply panel while no draft exists: says how long it takes. */
export const PREPARE_DRAFT_LABEL = "Prepare the reply (about 30 s)";

// ─── the minimal case page (12 Oct 2026) ────────────────────────────────────

/**
 * The three steps of a case, named the same on the page, in the add-in, in the queue
 * help and in the training.
 */
export const STEP_NAMES = {
  read: "Read the mail",
  check: "Check the draft",
  act: "Do the one action",
} as const;

/** The one status word of a draft line, lower case, from `lineOutcome`. */
export const LINE_STATUS_WORDS = {
  matched: "matched",
  confirm: "confirm",
  pick: "pick",
  noItem: "no item",
} as const;

export type LineStatusWord = (typeof LINE_STATUS_WORDS)[keyof typeof LINE_STATUS_WORDS];

/** The shadow-mode sentence, verbatim everywhere a reply is copied. */
export const SHADOW_SENTENCE =
  "Nothing is sent from here. Copy the reply and send it from Outlook.";

/** "Pick 2 items", "Pick 1 item". */
export function pickItemsLabel(count: number): string {
  return count === 1 ? "Pick 1 item" : `Pick ${nf.format(count)} items`;
}

/** "Confirm 2 items", "Confirm 1 item": every open line is a suggestion to confirm. */
export function confirmItemsLabel(count: number): string {
  return count === 1 ? "Confirm 1 item" : `Confirm ${nf.format(count)} items`;
}

/**
 * The labels of the one primary action and of the secondary actions under "More". The
 * queue and the training use the same words as literals.
 */
export const ACTION_LABELS = {
  reading: "Reading the mail",
  /** The badge while I2Q reads a case again after a customer confirmation (round 5). */
  readingAgain: "Reading again",
  setStatus: "Set status",
  nothing: "Nothing to do",
  takeOver: "Take over",
  take: "Take",
  release: "Release",
  findCustomer: "Find customer",
  chooseCustomer: "Pick the customer",
  /** "Confirm Acme Foundry, Aarhus". */
  confirmCustomer: (customer: string) => `Confirm ${customer}`,
  notThem: "Not them",
  why: "Why",
  // The deeper search (round 2, 13 Oct 2026): reject a proposal, and ask for the search again.
  notThisOne: "Not this one",
  lookAgain: "Look again",
  // Round 4 (12 Oct 2026): the customer menu and the row menu.
  changeCustomer: "Change customer",
  customerActions: "Customer actions",
  /** The visible word of the customer menu while the customer is certain. */
  change: "Change",
  changeItem: "Change item",
  /** "Line actions for 3 seal kits": the row menu's aria label. */
  lineActions: (asked: string) => `Line actions for ${asked}`,
  setPrice: "Set price",
  changePrice: "Change price",
  undoPrice: "Undo price",
  savePrice: "Save price",
  /**
   * The row menu's entry that opens the quantity and unit correction (sales review of
   * 12 Oct 2026, U-01: the dialog was reachable only through the line details).
   */
  correctQuantity: "Correct quantity",
  cancel: "Cancel",
  accept: acceptMatchedLabel,
  pick: pickItemsLabel,
  confirmLines: confirmItemsLabel,
  pickOneByOne: "Pick one by one",
  confirmLine: "Confirm",
  pickItem: "Pick item",
  noItem: "No item",
  addLine: "Add line",
  undo: "Undo",
  /** "Check: Price approval needed". */
  check: (reason: string) => `Check: ${reason}`,
  approvePrice: "Approve price",
  prepare: "Prepare the reply",
  prepareAgain: "Prepare again",
  copy: "Copy the reply",
  copyAnyway: "Copy anyway",
  copyAgain: "Copy again",
  editText: "Edit text",
  doneEditing: "Done editing",
  undoEdits: "Undo my edits",
  doneAndNext: "Mark done and next",
  markDone: "Mark done",
  notForUs: "Not for us",
  waitingCustomer: "Waiting for customer",
  reopen: "Reopen",
  reopenAsInquiry: "Reopen as inquiry",
  nextCase: "Next case",
  tryAgain: "Try again",
  giveFeedback: "Give feedback",
  shortcuts: "Shortcuts",
  more: "More",
  showDetails: "Show details",
  /** "Details (2)". */
  details: (count: number) => `Details (${nf.format(count)})`,
  openInI2q: "Open in I2Q",
  // The ease round (13 Oct 2026): quantity for many lines, the approval on the line, the checklist.
  setQuantityAll: "Set quantity for all lines",
  saveQuantity: "Save quantity",
  /** "Approve the price of line 2": the one action on a held price, for an approver. */
  approvePriceOf: (line: number) => `Approve the price of line ${nf.format(line)}`,
  takeBackApproval: "Take back approval",
  /** "Open points (3)": the checklist in the bar. */
  openPoints: (count: number) => `Open points (${nf.format(count)})`,
  goToDraft: "Go to the draft",
} as const;

/** The toast after a certain customer was replaced by a pick (round 4). */
export function customerChangedText(name: string): string {
  return `${name} is the customer now. Prices are read again for this account.`;
}

/**
 * The toast after "No item" on a row (tester round 5, 12 Oct 2026): the row moves into
 * the folded group "Nothing to quote" and a person who pressed it by mistake found no
 * Undo. The toast carries the Undo; the row menu keeps its own.
 */
export const NO_ITEM_TOAST = {
  title: "No item for this line",
  description: "The row is under Nothing to quote. Undo here or in the row menu.",
} as const;

/**
 * The words of the rules that hold a case, by rule id, for the one action ("Check:
 * Competitor on the sender list") and the queue (tester round 5, 12 Oct 2026: "Held by a
 * rule" named nothing). The price rules keep their status words in lib/case-status.ts.
 */
export const HELD_RULE_WORDS: Record<string, string> = {
  "gate.competitor": "Competitor on the sender list",
  "gate.sanctioned": "Sanctioned country",
  "gate.portal": "Portal customer",
  "gate.uk_wgg": "Quoted from Germany",
  "gate.uk_export": "UK export",
  "gate.media": "Media item",
  "gate.service_visit": "Service visit",
  "gate.out_of_scope_group": "Outside spare parts sales",
  "gate.doa_limit": "Approval limit",
  "gate.hsn": "HSN missing",
  "gate.signal_review": "Signal code check",
  "gate.signal_generic": "Generic item",
  "gate.successor": "Successor item",
  "gate.evaluation_failed": "Rule check failed",
  "header.pool": "Sales pool",
  "header.not_available": "Header field missing",
  "header.from_last_quotation": "From last quotation",
  "pricing.freight": "Freight",
  "pricing.lead_time": "Long lead time",
  "pricing.after_requested": "Ships late",
  "pricing.ship_today": "No lead time",
  "pricing.price_age": "Old price",
  "pricing.vs_last_quote": "Price vs last quotation",
};

/** The words of a holding rule, or null for a rule without words of its own. */
export function heldRuleWord(ruleId: string | null | undefined): string | null {
  return (
    (ruleId && Object.hasOwn(HELD_RULE_WORDS, ruleId) ? HELD_RULE_WORDS[ruleId] : null) ?? null
  );
}

/** The queue's action words (lane 2 uses the literals; listed here so they stay one set). */
export const QUEUE_ACTION_WORDS = [
  "Reading",
  "Could not read",
  "Nothing to do",
  "Find customer",
  "Confirm",
  "Pick n",
  "Check",
  "Prepare reply",
  "Copy reply",
] as const;

/**
 * "3 of 4 found · 1 line removed" over the draft lines. Without lines the sentence says
 * where the request probably is (hunt round 2, Q-28 b), from `noLines`.
 */
export function draftCaptionText(
  found: number,
  total: number,
  removed: number,
  noLines: NoLinesFacts = {},
): string {
  if (total === 0) {
    return removed > 0 ? `No lines · ${removedLinesText(removed)}` : noLinesSentence(noLines);
  }
  const head = `${nf.format(found)} of ${nf.format(total)} found`;
  return removed > 0 ? `${head} · ${removedLinesText(removed)}` : head;
}

function removedLinesText(count: number): string {
  return `${nf.format(count)} ${count === 1 ? "line" : "lines"} removed`;
}

/**
 * Under the draft: the parts an earlier message of the mail names while the newest part
 * has lines of its own (bug hunt 14 Oct 2026, Q-01 and Q-02). They are no lines.
 */
export const EARLIER_MENTIONS_CAPTION = "Named in the earlier mails";

/** The sentence under that caption. */
export const EARLIER_MENTIONS_NOTE =
  "These items are not in the draft. Add a line only when the customer asks for them now.";

/**
 * One earlier mention: the number as written, the catalogue item when it differs, its
 * name, the quantity, and our quotation it is already on. "4711-02 · Bearing housing ·
 * qty 2 · on SQ0099001".
 */
export function earlierMentionText(mention: {
  written: string;
  item?: string | null;
  name?: string | null;
  qty?: number | null;
  ordered?: string | null;
}): string {
  const item = mention.item && mention.item !== mention.written ? mention.item : null;
  return [
    mention.written,
    item,
    mention.name ?? null,
    mention.qty != null ? `qty ${nf.format(mention.qty)}` : null,
    mention.ordered ? `on ${mention.ordered}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

// ─── the draft as a triage table (round 2, 12 Oct 2026) ─────────────────────

/**
 * The three row groups of the draft, in the order they appear: what needs the sales
 * person first, then what I2Q is sure of or a person decided, then what is not quoted.
 */
export const DRAFT_GROUPS = {
  needsYou: "Needs you",
  matched: "Matched",
  nothingToQuote: "Nothing to quote",
} as const;

export type DraftGroup = (typeof DRAFT_GROUPS)[keyof typeof DRAFT_GROUPS];

/** The one sentence on each group row, next to the group's icon. */
export const DRAFT_GROUP_SENTENCES: Record<DraftGroup, string> = {
  [DRAFT_GROUPS.needsYou]: "Confirm or pick these first",
  [DRAFT_GROUPS.matched]: "I2Q is sure or you decided. Nothing to do here",
  [DRAFT_GROUPS.nothingToQuote]: "No item, or the customer took it back",
};

/**
 * The Matched group while a row I2Q matched still waits for Accept (round 5, 12 Oct 2026):
 * the big button says "Accept 2 matched items", so the group row must not say "Nothing to
 * do here".
 */
export const MATCHED_TO_ACCEPT_SENTENCE = "I2Q is sure. Accept these rows with the big button";

/**
 * The sentence of a group row. `undecidedMatched`: the case is open and at least one
 * matched row has no decision yet (the big button offers Accept).
 */
export function draftGroupSentence(group: DraftGroup, undecidedMatched: boolean): string {
  return group === DRAFT_GROUPS.matched && undecidedMatched
    ? MATCHED_TO_ACCEPT_SENTENCE
    : DRAFT_GROUP_SENTENCES[group];
}

/** The words of the draft cells that are not a figure (the table and the phone cards). */
export const DRAFT_CELL_WORDS = {
  /** Behind the chevron of a matched row and in the row menu: the item search for another item. */
  changeItem: ACTION_LABELS.changeItem,
  /** The label of the alert icon on a quantity with an issue. */
  qtyIssue: "Quantity to check",
  /**
   * The quantity cell when the mail names none (sales review of 12 Oct 2026, U-05: an
   * empty cell looked like a quantity that was never read).
   */
  qtyMissing: "quantity missing",
} as const;

/**
 * The badge of a line that stands in for the request from an attached document (the flag
 * `from_parts_list` keeps it a suggestion). A position of our own quotation, attached to
 * a mail that asks to quote it again, is not a parts list (sales review of 12 Oct 2026,
 * X-02).
 */
export const STAND_IN_WORDS = {
  // ste-allow: product-term (a parts list is a domain noun, like spare parts)
  partsList: "From a parts list",
  ourQuotation: "From our quotation",
} as const;

/**
 * The badge words for the document a stand-in line was read from (`source.doc_type` of an
 * attachment source). An older draft without the document type reads as a parts list.
 */
export function standInWord(docType: string | null | undefined): string {
  return docType === "our_quotation" ? STAND_IN_WORDS.ourQuotation : STAND_IN_WORDS.partsList;
}

/**
 * The chip of a line that is a position of our quotation (`source.kind` "quotation"): an
 * amend, or an order or a signed copy of our quotation (decision 1 of 14 Oct 2026, hunt
 * round 3). "From our quotation SQ0990456"; without a number the word of the stand-in.
 */
export function quotationLineChip(number: string | null | undefined): string {
  const doc = number?.trim();
  return doc ? `${STAND_IN_WORDS.ourQuotation} ${doc}` : STAND_IN_WORDS.ourQuotation;
}

/**
 * The banner text of an order or a signed copy of our quotation (decision 1 of 14 Oct
 * 2026): the lines are the positions of our quotation, each a suggestion.
 */
export const ORDER_FOLLOW_UP_TEXT =
  "The lines are the positions of our quotation. Check them against the order.";

/**
 * The note of a case where the delivery terms of the mail and of the customer master differ
 * (hunt round 3, Q-22). The comparison is by the Incoterm code (`incotermCode`). Null when
 * one side is missing, has no code, or both codes are the same.
 */
export function deliveryTermsNote(
  mail: string | null | undefined,
  master: string | null | undefined,
): string | null {
  const mailTerms = mail?.trim() ?? "";
  const masterTerms = master?.trim() ?? "";
  const mailCode = incotermCode(mailTerms);
  const masterCode = incotermCode(masterTerms);
  if (!mailCode || !masterCode || mailCode === masterCode) {
    return null;
  }
  return `Delivery terms differ: the mail says ${mailTerms}, the customer master says ${masterTerms}.`;
}

/** The 11 Incoterms 2020 codes. */
const INCOTERM_CODES = [
  "EXW",
  "FCA",
  "FAS",
  "FOB",
  "CFR",
  "CIF",
  "CPT",
  "CIP",
  "DAP",
  "DPU",
  "DDP",
] as const;

/** Spelled-out Incoterms; a longer name before a shorter name that it contains. */
const INCOTERM_NAMES: readonly [RegExp, string][] = [
  [/\bex[\s-]*works\b/, "EXW"],
  [/\bfree\s+carrier\b/, "FCA"],
  [/\bfree\s+alongside\s+ship\b/, "FAS"],
  [/\bfree\s+on\s+board\b/, "FOB"],
  [/\bcost\s*,?\s*insurance\s+(and|&)\s+freight\b/, "CIF"],
  [/\bcost\s+(and|&)\s+freight\b/, "CFR"],
  [/\bcarriage\s+(and|&)\s+insurance\s+paid\b/, "CIP"],
  [/\bcarriage\s+paid\s+to\b/, "CPT"],
  [/\bdelivered\s+at\s+place\s+unloaded\b/, "DPU"],
  [/\bdelivered\s+at\s+place\b/, "DAP"],
  [/\bdelivered\s+duty\s+paid\b/, "DDP"],
];

/**
 * The Incoterm code of a delivery terms text (review of round 3, L4): the first of the 11
 * codes that stands as a whole word ("Incoterms 2020 FCA" is FCA), else a spelled-out name
 * ("Ex Works" is EXW). Null when the text names none.
 */
export function incotermCode(terms: string | null | undefined): string | null {
  const text = (terms ?? "").toUpperCase();
  let first: { code: string; at: number } | null = null;
  for (const code of INCOTERM_CODES) {
    const at = text.search(new RegExp(`(^|[^A-Z])${code}([^A-Z]|$)`));
    if (at >= 0 && (first === null || at < first.at)) {
      first = { code, at };
    }
  }
  if (first) {
    return first.code;
  }
  const lower = (terms ?? "").toLowerCase();
  return INCOTERM_NAMES.find(([pattern]) => pattern.test(lower))?.[1] ?? null;
}

/**
 * The parts of the customer context under the customer line of an agent case (sales
 * review of 12 Oct 2026, U-02): the account to quote, the sender who is the agent, and
 * the end customer as the mail names it. Before, the agent and the end customer sat in
 * the Details sheet only.
 */
export const CUSTOMER_PARTY_LABELS = {
  account: "Account",
  accountNone: "none yet",
  /** The mark after an account nobody confirmed and I2Q is not certain of. */
  accountSuggested: "suggested",
  sender: "Sender",
  /** The mark after the sender's name: an agent or distributor, or a company of the group. */
  agent: "agent",
  sisterCompany: "sister company",
  endCustomer: "End customer",
  endCustomerNote: "as named in the mail",
} as const;

/**
 * The two words on the customer line of a customer that needs no confirmation (sales
 * review of 12 Oct 2026, C-06). "Confirmed": I2Q is certain of the account from its own
 * evidence. "Confirmed by sales": a person picked or confirmed the account on this case.
 * One word had named both, so nobody could see who decided.
 */
export const CUSTOMER_CONFIRMED_WORDS = {
  byI2q: "Confirmed",
  bySales: "Confirmed by sales",
} as const;

/**
 * The heading over the other accounts in "Why" on the customer line (sales review of
 * 12 Oct 2026, C-02). On the mail of an agent the accounts of the agent's past are its
 * earlier end customers: they are listed and named as that, never offered as the account.
 */
export const CUSTOMER_WHY_WORDS = {
  others: "Other possible customers",
  earlierOfSender: "Earlier end customers of this sender",
} as const;

/** The word for a certain customer: a person decided (`bySales`), or I2Q is certain. */
export function customerConfirmedWord(bySales: boolean): string {
  return bySales ? CUSTOMER_CONFIRMED_WORDS.bySales : CUSTOMER_CONFIRMED_WORDS.byI2q;
}

/**
 * The queue's empty state while the date window hides open work (sales review of
 * 12 Oct 2026, U-04): the Today filter is named in the title, and one click widens the
 * dates alone. "Show everyone, any date" stays for the case where the assignee filter
 * hides work too.
 */
export const QUEUE_EMPTY_WORDS = {
  nothingToday: "Nothing to review today",
  showAnyDate: "Show any date",
  showEveryoneAnyDate: "Show everyone, any date",
  /** "Received: today" in the chip row, removable. */
  received: (window: string) => `Received: ${window}`,
} as const;

/**
 * The price cell when no price applies (sales review of 12 Oct 2026, P-01: 92 lines said
 * "no price" and hid why). The reason, keyed by the gap of the line's price status:
 * the customer first (to confirm, or to find when none is suggested), the quantity
 * first, an item of another catalogue, no item yet, F&O holds no price, its price
 * expired, it holds several currencies, or no lookup ran yet.
 *
 * Each cell says the same as the long sentence of the line (`priceSentence` in
 * `lib/work-labels.ts`): the same reason, in fewer words (review of the fixes, M6).
 */
export const PRICE_GAP_WORDS = {
  no_customer: "Price after you confirm the customer",
  no_customer_open: "Price after you find the customer",
  no_quantity: "Price needs a quantity",
  other_entity: "Item in another catalogue",
  no_item: "No item yet",
  no_price: "No price in F&O",
  expired: "No valid price in F&O",
  currency_ambiguous: "Several currencies on file",
  not_looked_up: "Not looked up yet",
} as const;

export type PriceGap = keyof typeof PRICE_GAP_WORDS;

/**
 * The lead cell when no lead time is shown (sales review of 12 Oct 2026, P-02: one
 * "not fetched" stood for three states). F&O was read and holds none, or no lookup ran.
 */
export const LEAD_GAP_WORDS = {
  none_in_fo: "No lead time in F&O",
  not_looked_up: "Not looked up yet",
} as const;

/**
 * Under a lead time from an earlier quotation or mail offer (P-02): where it comes from,
 * so it reads as context, never as today's lead time.
 */
export const LEAD_ORIGIN_WORDS = {
  quotation: (id: string, day: string) => `From quotation ${id} on ${day}`,
  quotationUndated: (id: string) => `From quotation ${id}`,
  /** The quotation's number is not known: the day alone, or nothing but "earlier". */
  quotationUnnamed: (day: string) => `From an earlier quotation on ${day}`,
  quotationUnknown: "From an earlier quotation",
  mailOffer: (day: string) => `From a mail offer on ${day}`,
  mailOfferUndated: "From an earlier mail offer",
  /** The long figures under a historic lead time: what it is for. */
  contextOnly: "Context only. Confirm the lead time first.",
  /** Under a lead time from the item master (13 Oct 2026): which of its three fields. */
  itemMasterSales: "Item master: sales lead time",
  itemMasterPurchase: "Item master: purchase lead time",
  itemMasterInventory: "Item master: inventory lead time",
  /** The long figures under an item master lead time. */
  aboutInReply: (days: number) =>
    `In the reply: about ${nf.format(days)} ${days === 1 ? "day" : "days"}, to be confirmed.`,
} as const;

/** What the icon of the lead cell stands for when a figure is next to it (P-02). */
export const LEAD_FIGURE_LABELS = {
  known: "Lead time in F&O",
  historic: "Lead time from an earlier quotation, context only",
  /** A master data figure: the reply says "about, to be confirmed". */
  itemMaster: "Lead time from the item master, to be confirmed",
} as const;

/** The column headers of the draft table, in order. */
export const DRAFT_COLUMNS = {
  status: "Status",
  asked: "Customer asked",
  proposed: "I2Q proposes",
  qty: "Qty",
  price: "Price",
  lead: "Lead",
} as const;

/** "1 to pick", "2 to confirm", "3 matched", "1 no item": the count badges of the strip. */
export function draftCountText(
  kind: "pick" | "confirm" | "matched" | "noItem",
  count: number,
): string {
  const n = nf.format(count);
  switch (kind) {
    case "pick":
      return `${n} to pick`;
    case "confirm":
      return `${n} to confirm`;
    case "matched":
      return `${n} matched`;
    case "noItem":
      return `${n} no item`;
  }
}

// ─── the ease round (13 Oct 2026): what blocks a reply, and what a save says ──

/** The titles of the checklist. */
export const CHECKLIST_WORDS = {
  must: "Before you can prepare the reply",
  open: "Open in the reply if you leave them",
} as const;

/**
 * The data gap block: one lead sentence, the reason by blocker kind, and the sentence over
 * the two ways out. I2Q says what it found, never that F&O has nothing.
 */
export const DATA_GAP_WORDS = {
  lead: "I2Q cannot finish this case by itself.",
  customer_missing: "I2Q found no account in F&O for this sender.",
  /** Hunt round 2 (Q-14): on an agent's mail the missing account is named by its party. */
  customer_missing_agent: "I2Q found no account in F&O for the agent.",
  customer_missing_end_customer: "I2Q found no account in F&O for the end customer.",
  lines_to_pick: "I2Q found no item in F&O for these lines.",
  price_missing: "F&O has no valid price for these lines.",
  ways: "Set it by hand, or set the status of the case.",
} as const;

/** The approval of a held price, on the line and in Details. */
export const PRICE_APPROVAL_WORDS = {
  approvedBy: (name: string, day: string) => `Price approved by ${name} on ${day}`,
  takeBack: "Take back",
  madeBefore: "Made before the last change.",
} as const;

/** The quantity offer over the draft, and the default quantity of a line. */
export const QUANTITY_WORDS = {
  /**
   * The mark on a quantity the mail did not give (product owner, 14 Oct 2026): I2Q uses 1,
   * prices the line for 1 and the reply asks the customer to confirm the quantity.
   */
  defaultUsed: "Quantity not given, 1 used",
  dialogTitle: "Set quantity for all lines",
  dialogNote: "I2Q uses this quantity for the prices, the checks and the reply. Nothing is sent.",
  scopeAll: "All lines",
  scopeMissing: "Lines without a given quantity",
  inlineHint: "Type a whole number. Enter saves. Tab goes to the next line.",
  corrected: "Corrected by sales",
  /**
   * The mark on a quantity the customer wrote for the same part in an earlier message of the
   * thread, not in the newest mail (`qty_source` "earlier_mail"; a suggestion to check).
   */
  earlierMail: "Quantity from an earlier mail",
  /** Under "Lines" of the dialog, when the chosen lines hold a quantity already. */
  replaces: (n: number) =>
    n === 1
      ? "1 of these lines has a quantity already. The new quantity replaces it."
      : `${nf.format(n)} of these lines have a quantity already. The new quantity replaces them.`,
} as const;

/** What a blocker sentence needs: the facts of one `CaseBlocker` and of the viewer. */
export interface BlockerWordFacts {
  /** The count of the blocker: its lines, or the accounts offered. */
  n: number;
  /** The row number of the first line, as the page shows it ("Line 2"). */
  k: number;
  /** `customer_to_confirm` with `one_click`: the name of that account. Else null. */
  name?: string | null;
  /** `price_held`: the viewer may approve a price. */
  mayApprove?: boolean;
  /** `price_held`: the approval of the first line no longer matches the line. */
  stale?: boolean;
  /** `rule_hold`: the rule, its sentence and what sales do next. */
  ruleId?: string | null;
  message?: string | null;
  action?: string | null;
  /** `picture_cut`: the picture and how many of its rows were read. */
  file?: string | null;
  rowsRead?: number | null;
}

/** "1 line has" or "3 lines have", for the sentences that start with the count. */
function linesHave(n: number): string {
  return n === 1 ? "1 line has" : `${nf.format(n)} lines have`;
}

/** "1 line" or "3 lines". */
function linesOf(n: number): string {
  return n === 1 ? "1 line" : `${nf.format(n)} lines`;
}

/** The word of a holding rule that has no words of its own (`HELD_RULE_WORDS`). */
export const HELD_BY_A_RULE = "Held by a rule";

/**
 * The one sentence of each thing that stands between a case and a reply, by blocker kind
 * of the case detail (`blockers`). It is the link in the action bar and in the checklist:
 * what is open, then the control that resolves it.
 */
export const BLOCKER_WORDS: Record<CaseBlocker["kind"], (facts: BlockerWordFacts) => string> = {
  customer_missing: () => "No customer account: find the customer.",
  customer_to_confirm: ({ name }) =>
    name
      ? `Customer to confirm: confirm ${name} or pick another.`
      : "Customer to confirm: pick the customer.",
  no_lines: () =>
    "No lines found in the mail: add a line, or prepare a reply that asks the customer.",
  lines_to_pick: ({ n }) => `${linesHave(n)} no item: pick an item or press No item.`,
  lines_to_confirm: ({ n }) =>
    n === 1
      ? "1 line to confirm: check the item, then confirm."
      : `${nf.format(n)} lines to confirm: check the items, then confirm.`,
  lines_to_accept: ({ n }) =>
    n === 1
      ? "I2Q is sure of 1 line: accept it."
      : `I2Q is sure of ${nf.format(n)} lines: accept them.`,
  price_held: ({ n, k, mayApprove, stale }) => {
    if (stale) {
      const head = `The price approval of line ${nf.format(k)} no longer matches: the price or the quantity changed.`;
      return mayApprove
        ? `${head} Approve the price again.`
        : `${head} Ask a manager to approve it again.`;
    }
    const head =
      n === 1
        ? `Line ${nf.format(k)} is held: the price is below the limit.`
        : `${nf.format(n)} lines are held: the price is below the limit.`;
    if (mayApprove) {
      return n === 1
        ? `${head} Approve the price or change it.`
        : `${head} Approve each price or change it.`;
    }
    return n === 1
      ? `${head} Change the price, or ask a manager to approve it.`
      : `${head} Change each price, or ask a manager to approve it.`;
  },
  rule_hold: ({ ruleId, message, action }) =>
    [`${heldRuleWord(ruleId) ?? HELD_BY_A_RULE}.`, (message ?? "").trim(), (action ?? "").trim()]
      .filter(Boolean)
      .join(" "),
  quantity_missing: ({ n }) => `No quantity on ${linesOf(n)}: set the quantity.`,
  quantity_to_check: ({ n }) => `Quantity or unit to check on ${linesOf(n)}: correct the quantity.`,
  price_missing: ({ n }) => `${linesHave(n)} no price: set a price or press No item.`,
  customer_instruction: ({ message }) => customerInstructionText(message ?? ""),
  // Decision D3 (19 Oct 2026): the reader stops after about 30 rows of a long list, and a
  // DISA list numbers bottom-up, so the asked rows can be the ones left out.
  picture_cut: ({ file, rowsRead }) => pictureCutText(file, rowsRead),
};

/** The open point of a picture whose list was read only in part (D3). */
export function pictureCutText(
  file: string | null | undefined,
  rowsRead: number | null | undefined,
): string {
  const name = file?.trim() || "the picture";
  return typeof rowsRead === "number" && rowsRead > 0
    ? `Only the top ${nf.format(rowsRead)} rows of ${name} were read. Rows below them are missing: add them with Add line.`
    : `Only the top rows of ${name} were read. Rows below them are missing: add them with Add line.`;
}

/**
 * The second sentence of an open point, shown after the first when it is the sentence in
 * the bar: what the reply says when the point stays open. `no_lines` has none.
 */
export const BLOCKER_OPEN_NOTES: Partial<Record<CaseBlocker["kind"], string>> = {
  quantity_missing: "Without it, the reply asks the customer for the quantity.",
  quantity_to_check: "Without it, the reply says quantity and unit to confirm.",
  price_missing: "Without it, the reply says price to confirm.",
};

/** The toasts after a save. `line` is the row number. */
export const SAVE_TOASTS = {
  quantity: {
    title: "Quantity saved",
    text: (line: number, value: string) => `Line ${nf.format(line)}: ${value}.`,
  },
  quantityAll: {
    title: (n: number) => `Quantity set on ${nf.format(n)} ${n === 1 ? "line" : "lines"}`,
    text: "Undo puts back the quantities from before.",
  },
  noChange: { title: "No change", text: "The value is the same as before." },
  price: { title: "Price saved", text: "Checks, the quotation preview and the reply use it now." },
  lineConfirmed: {
    title: "Line confirmed",
    text: (line: number, item: string) => `Line ${nf.format(line)}: ${item}.`,
  },
  itemPicked: {
    title: "Item picked",
    text: (line: number, item: string) => `Line ${nf.format(line)}: ${item}.`,
  },
  linesConfirmed: {
    title: "Lines confirmed",
    text: (n: number) => `${nf.format(n)} ${n === 1 ? "line" : "lines"} confirmed. Undo per line.`,
  },
  customer: { title: "Customer confirmed" },
  priceApproved: {
    title: "Price approved",
    text: (line: number) => `Line ${nf.format(line)} is not held any more.`,
  },
  approvalTakenBack: {
    title: "Approval taken back",
    text: (line: number) => `Line ${nf.format(line)} is held again.`,
  },
  statusDone: "Marked done",
  statusNotForUs: "Set to not for us",
  statusWaiting: "Set to waiting for customer",
  statusOpen: "Reopened",
  taken: "The case is yours now.",
  failed: { title: "Not saved" },
} as const;

/** The one-time hint over the queue for a person who is new. */
export const QUEUE_START_HINT = {
  title: "New here?",
  text: "Start with Ready to quote. I2Q is sure of every line: these cases can be finished now.",
  action: "Show them",
  dismiss: "Not now",
} as const;

/**
 * The empty state of the tab "Ready to quote": no open case under the filters is in the
 * state the tab lists.
 */
export const READY_EMPTY_WORDS = {
  title: "Nothing is ready to quote",
  text: "No open case with the current filters has every line matched and priced. Look under To review.",
} as const;

// ─── the bug hunt of 14 Oct 2026, round 2 (labels of record, CONTRACTS.md 3.5) ──

/**
 * The chip on a line whose number is not in the catalogue of the case's entity but in the
 * catalogue of another entity (decision 5): "Only in UK: 4711-02". A hint, never the item.
 */
export function otherEntityChipText(entry: { entity: string; item: string }): string {
  return `Only in ${entry.entity.trim().toUpperCase()}: ${entry.item}`;
}

/** The HSN words on an India line (decision 10). `not_known` says nothing. */
export const HSN_WORDS = {
  code: (code: string) => `HSN ${code}`,
  missing: "HSN missing",
  placeholder: "HSN placeholder",
  malformed: "HSN not valid",
} as const;

/**
 * The HSN chip of a line: the code when F&O has a valid one, else what is wrong with it;
 * null when the entity needs no HSN or the column is not mirrored.
 */
export function hsnChipText(
  code: string | null | undefined,
  status: HsnStatus | null | undefined,
): string | null {
  switch (status) {
    case "present":
      return code ? HSN_WORDS.code(code) : null;
    case "missing":
      return HSN_WORDS.missing;
    case "placeholder":
      return HSN_WORDS.placeholder;
    case "malformed":
      return HSN_WORDS.malformed;
    default:
      return null;
  }
}

/**
 * The banner of a price question or a discount request on our quotation (decision 7):
 * "Customer asks about price on SQ0099001, SQ0099002". A follow-up, never a new quote.
 */
export function priceQuestionText(numbers: readonly string[]): string {
  return `Customer asks about price on ${numbers.join(", ")}`;
}

/**
 * The close offer of a system notification or a newsletter (decision 8). The button sends
 * the status "not for us" with the note; I2Q never closes such a case by itself.
 */
export const CLOSE_OFFER_WORDS = {
  button: "Close: not an inquiry",
  note: "Not an inquiry",
  /** The chip of the queue filter. */
  filter: "Not an inquiry",
} as const;

/** The chip of a line whose item does not fit the row (Q-09, Q-10). Never one-click. */
export const MISMATCH_WORDS: Record<LineMismatch, string> = {
  size: "Size differs",
  description: "Description differs",
  // ste-allow: screen-label (label of record, CONTRACTS.md 3.5: the customer's own word)
  customer_says_wrong: "Customer says wrong part",
  assembly_for_position: "Whole assembly for a position",
};

/** The mismatch chip of a line, or null. */
export function mismatchChipText(mismatch: string | null | undefined): string | null {
  return mismatch && Object.hasOwn(MISMATCH_WORDS, mismatch)
    ? MISMATCH_WORDS[mismatch as LineMismatch]
    : null;
}

/**
 * The reason labels of the real reason a line has no item (Q-28 a): the server's why
 * says the full sentence, the label names the reason in the line's details.
 */
export const REASON_LABELS = {
  not_in_catalogue: "Not in the catalogue",
  other_entity: "In another entity",
  typo_candidate: "Number differs by one character",
  blocked_item: "Blocked in F&O",
  mismatch: "Item does not fit the row",
} as const;

/** The label of a reason code of hunt round 2, or null for any other code. */
export function reasonLabel(reasonCode: string | null | undefined): string | null {
  return reasonCode && Object.hasOwn(REASON_LABELS, reasonCode)
    ? REASON_LABELS[reasonCode as keyof typeof REASON_LABELS]
    : null;
}

/** The basis word of a match by the padded US number form (decision 6: 97703 as 0977030). */
export const PADDED_NUMBER_WORD = "US number form";

/** The words of the pictures and of the files inside a file (decision 1). */
export const PICTURE_WORDS = {
  inline: "Picture in the mail body",
  /** A file inside an attached mail or Office file: "In order.eml: page1.jpg". */
  child: (parent: string, file: string) => `In ${parent}: ${file}`,
  /**
   * The entry for the files inside an attached file that I2Q could not list (review of
   * round 3, L1: an attached mail of about 64 MB or more). Its note says why.
   */
  filesInside: (parent: string) => `Files inside ${parent}`,
  // ste-allow: screen-label (label of record, CONTRACTS.md 3.5)
  noRequest: "I2Q read this picture: no part or quantity found",
  holdsPictures: "No item lines in the text. I2Q read the pictures in the file.",
  readOnce: "Read once from the scan",
  /**
   * Decision D2 (19 Oct 2026): a row of a picture the mail text does not ask for. A
   * customer pasted a whole parts list to show where the item sits: the other rows are
   * context, never lines, never priced.
   */
  notAsked: "From the picture, not asked",
  notAskedFold: (n: number) =>
    `From the picture, not asked: ${nf.format(n)} ${n === 1 ? "row" : "rows"}`,
  notAskedNote: "The mail text does not ask for these rows. I2Q made no line of them.",
  /** Decision D2: a reply's picture that only the quoted earlier mail shows. */
  quotedPicture: "In the quoted mail: not read again",
} as const;

/**
 * Decision D1 (19 Oct 2026): a colleague's reply in the thread of an open case is filed on
 * that case, not as a case of its own (a product manager named the hose number for one
 * open line, and the reply became a case with 33 picture lines). The head case shows it as
 * a note; its numbers are candidates on the open line, never an item, never accepted.
 */
export const COLLEAGUE_NOTE_WORDS = {
  title: (name: string | null | undefined) =>
    `${name?.trim() || "A colleague"} replied in this thread`,
  filed: "I2Q filed this reply on the case. Its item numbers are candidates only.",
  offered: (line: number) => `Candidate on line ${nf.format(line)}`,
  context: "Not on a line: check the open lines",
  unknown: "Not in the catalogue",
  open: "Open the reply",
} as const;

/** The words of a long list (decision 4): every row is shown, grouped by its source. */
export const LONG_LIST_WORDS = {
  group: (source: string, n: number) => `${source}: ${nf.format(n)} ${n === 1 ? "line" : "lines"}`,
  fold: (n: number) => `Show all ${nf.format(n)} lines`,
  cut: (n: number) => `Long list: I2Q read the first ${nf.format(n)} lines only`,
  mailText: "Mail text",
} as const;

/** A group of the draft with more rows than this starts folded. */
export const LONG_LIST_FOLD_AT = 50;

/** The chip on the party whose account is quoted first on an agent's mail (decision 2). */
export const QUOTED_FIRST = "Quoted first";

/** The open point of a price account or a discount instruction in the mail (Q-18). */
export function customerInstructionText(note: string): string {
  return `Customer instruction: ${note.trim()}`;
}

/**
 * Which account F&O lacks (Q-14): the sender's, the agent's or the end customer's. An
 * older blocker without a party reads as the sender.
 */
export function customerMissingText(party: string | null | undefined): string {
  switch (party) {
    case "agent":
      return DATA_GAP_WORDS.customer_missing_agent;
    case "end_customer":
      return DATA_GAP_WORDS.customer_missing_end_customer;
    default:
      return DATA_GAP_WORDS.customer_missing;
  }
}

/** The sentences of a case without lines (Q-28 b), chosen from the fields of the case. */
export const NO_LINES_WORDS = {
  plain: "No lines found in the mail",
  firstMail: "No lines found in this mail. The request may be in the first mail.",
  // ste-allow: product-term (the quoted part of a mail, as in "Read all")
  quoted: "No lines found in this mail. The request is in the quoted part.",
  notRead: "No lines found in this mail. An attachment was not read.",
} as const;

/** The facts the no-lines sentence is chosen from. */
export interface NoLinesFacts {
  /** The case is a reply in a thread and its first mail is known. */
  firstMail?: string | null;
  /** Parts named in the quoted or forwarded part of the mail. */
  earlierMentions?: number;
  /** An attachment carries a note that it was not read. */
  attachmentNotRead?: boolean;
}

/** The one sentence of a case without lines: where the request probably is. */
export function noLinesSentence(facts: NoLinesFacts): string {
  if (facts.firstMail) {
    return NO_LINES_WORDS.firstMail;
  }
  if ((facts.earlierMentions ?? 0) > 0) {
    return NO_LINES_WORDS.quoted;
  }
  if (facts.attachmentNotRead) {
    return NO_LINES_WORDS.notRead;
  }
  return NO_LINES_WORDS.plain;
}

/** The pick customer dialog (Q-20): the accounts of the case above the search hits. */
export const PICK_CUSTOMER_WORDS = {
  onCase: "Accounts on this case",
  noMatch: "No customer matches the search. Try one word, a city or a postcode.",
} as const;
